import { queryOptions, useQuery } from '@tanstack/react-query';

import {
  DEFAULT_TENANT_CAPABILITIES,
  tenantSettingsApi,
  type TenantCapabilities,
} from '@/shared/api/tenantSettings';

/**
 * `GET /tenant-settings/capabilities` (`AM-202`): fetched once per session, any member of the
 * tenant - long `staleTime`, the same idiom as `appearanceQueryOptions`, since the tenant rarely
 * flips this. Invalidated by `CapabilitiesPage`'s own successful save, never only by the TTL.
 */
export const tenantCapabilitiesQueryKey = ['tenantSettings', 'capabilities'] as const;

export function tenantCapabilitiesQueryOptions() {
  return queryOptions({
    queryKey: tenantCapabilitiesQueryKey,
    queryFn: ({ signal }) => tenantSettingsApi.capabilities(signal),
    staleTime: 30 * 60_000,
  });
}

/**
 * The tenant's capabilities for every surface that only reads them (the navigation, the
 * "Facturable" checkboxes): while the query has not settled, or settled into an error, both read
 * as `true` - the historical behaviour, so nothing disappears for a tenant this predates. The one
 * surface that needs the real pending/error state to edit them, `CapabilitiesPage`, reads
 * {@link tenantCapabilitiesQueryOptions} directly instead of this hook.
 */
export function useTenantCapabilities(): TenantCapabilities {
  const capabilities = useQuery(tenantCapabilitiesQueryOptions());
  return capabilities.data ?? DEFAULT_TENANT_CAPABILITIES;
}
